import * as moment from 'moment';
import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { IFollowUpDonorForList } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ActionButtonRenderer } from './action-button-renderer';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { DonorAction } from '../donor-action';
import { Utility } from '@nkr/services/utility';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';

interface IProps {
    list: IFollowUpDonorForList[],
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onLoadingComplete: () => void,
    onGridReady: (e: GridReadyEvent) => void,
    onItemSelected: (id: number, action: DonorAction) => void
}

const CommonList = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'nextSurveyDate',
                    sort: 'asc' // 'asc'
                }
            ]
        });

        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
        props.onLoadingComplete();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells({ force: true });
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const cols: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: '#',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
            width: 55,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>,
            suppressSizeToFit: true
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            hide: props.isAdmin == false
        },
        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'gender',
            field: 'gender',
            headerName: 'Gender',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Gender</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            width: 105
        },
        {
            colId: 'birthDate',
            field: 'birthDate',
            headerName: 'Birth Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Birth Date</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'city',
            field: 'city',
            headerName: 'City',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>City</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
        },
        {
            colId: 'state',
            field: 'state',
            headerName: 'State',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>State</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            width: 85
        },
        {
            colId: 'donationDate',
            field: 'donationDate',
            headerName: 'Donated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donated</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'lastSurveyInstance',
            field: 'lastSurveyInstance',
            headerName: 'Last Survey',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Survey</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            width: 135,
            valueGetter: (params: ValueGetterParams) => params.data.lastSurveyInstanceId,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.data.lastSurveyInstance != '' ? params.data.lastSurveyInstance : '--'}</>
        },
        {
            colId: 'nextSurveyInstance',
            field: 'nextSurveyInstance',
            headerName: 'Next Survey',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next Survey</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            width: 135,
            valueGetter: (params: ValueGetterParams) => params.data.nextSurveyInstanceId,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.data.nextSurveyInstance != '' ? params.data.nextSurveyInstance : '--'}</>
        },
        {
            colId: 'nextSurveyDate',
            field: 'nextSurveyDate',
            headerName: 'nextSurveyDate',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Survey Trigger</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            width: 125,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IFollowUpDonorForList;

                if (moment(data.nextSurveyDate, 'MM/DD/YYYY').isSameOrBefore(moment())) {
                    return (
                        <span style={{ padding: 4.2 }} className='label label-full label-warning'>{data.nextSurveyDate}</span>
                    );
                }

                return (
                    <span style={{ padding: 4.2 }} className='label label-full label-default'>{data.nextSurveyDate}</span>
                );
            }
        },
        {
            colId: 'actionMenu',
            cellRendererFramework: (params: ICellRendererParams) =>
                <div style={{ width: '100%', textAlign: 'center' }}>
                    <ActionButtonRenderer {...params}
                        pullRight={true}
                        isAdmin={props.isAdmin}
                        isAdminLogin={props.isAdminLogin}
                        data={params.data}
                        onMenuToggle={handleMenuToggle}
                        onItemSelected={props.onItemSelected} />
                </div>,
            width: 80,
            cellClass: 'action-menu',
            headerClass: 'follow-up-list-header',
            suppressSizeToFit: true
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const list = props.list.length > 0 ? props.list : (props.isLoading ? null : []);
    const rowData = useMemo(() => list, [list]);

    return (
        <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            headerHeight={30}
            rowHeight={28}
            suppressCellSelection={true}
            // immutableData={true}
            overlayNoRowsTemplate={`No follow-up donors...`}
            overlayLoadingTemplate={`Loading follow-up donors...`}
            // getRowNodeId={(data: IFollowUpDonorForList) => data.id.toString()}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
            onSortChanged={onSortChanged}
        />
    );
}

export default CommonList;